/**
 * Swiss QR-bill validation + reference helpers (SIX Swiss Payment Standards,
 * QR-bill Implementation Guidelines). Pure and fully unit-tested.
 *
 * - IBAN: ISO 13616 mod-97 check.
 * - QR-IBAN: Swiss/LI IBAN whose QR-IID (positions 5–9) is in 30000–31999.
 * - QR reference (QRR): 27 digits, mod-10 recursive check digit (ESR algorithm).
 * - Creditor reference (SCOR): ISO 11649 RF reference, mod-97 check.
 */

export function normalizeIban(iban: string): string {
  return iban.replace(/\s+/g, "").toUpperCase();
}

export function isValidIban(raw: string): boolean {
  const iban = normalizeIban(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  // ISO 13616 mod-97: move first 4 chars to the end, letters → digits, mod 97 === 1.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    if (code >= 48 && code <= 57) {
      remainder = (remainder * 10 + (code - 48)) % 97;
    } else if (code >= 65 && code <= 90) {
      const n = code - 55; // A=10 … Z=35
      remainder = (remainder * 10 + Math.floor(n / 10)) % 97;
      remainder = (remainder * 10 + (n % 10)) % 97;
    } else {
      return false;
    }
  }
  return remainder === 1;
}

/** QR-IID: digits 5–9 of a Swiss/LI IBAN (after country + check digits). */
export function extractQrIid(iban: string): number | null {
  const normalized = normalizeIban(iban);
  if (!/^(CH|LI)\d{19}$/.test(normalized)) return null;
  return Number(normalized.slice(4, 9));
}

export function isQrIban(iban: string): boolean {
  const iid = extractQrIid(iban);
  return iid !== null && iid >= 30000 && iid <= 31999;
}

/** Mod-10 recursive check digit (Swiss ESR/QR-reference algorithm). */
export function mod10RecursiveCheckDigit(digits: string): string {
  if (!/^\d+$/.test(digits))
    throw new Error("mod10 recursive requires digits only");
  const TABLE = [0, 9, 4, 6, 8, 2, 7, 1, 3, 5] as const;
  let carry = 0;
  for (const ch of digits) carry = TABLE[(carry + Number(ch)) % 10]!;
  return String((10 - carry) % 10);
}

/** QR reference: 26 free digits + check digit = 27 total. */
export function buildQrReference(freeDigits: string): string {
  const clean = freeDigits.replace(/\s+/g, "");
  if (!/^\d{1,26}$/.test(clean))
    throw new Error("QR reference base must be 1–26 digits");
  const padded = clean.padStart(26, "0");
  return padded + mod10RecursiveCheckDigit(padded);
}

export function isValidQrReference(ref: string): boolean {
  const clean = ref.replace(/\s+/g, "");
  if (!/^\d{27}$/.test(clean)) return false;
  return mod10RecursiveCheckDigit(clean.slice(0, 26)) === clean[26];
}

/** ISO 11649 RF creditor reference: "RF" + 2 check digits + ≤21 alnum. */
export function buildCreditorReference(base: string): string {
  const clean = base.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z0-9]{1,21}$/.test(clean)) {
    throw new Error(
      "Creditor reference base must be 1–21 alphanumeric characters",
    );
  }
  const withPlaceholder = `${clean}RF00`;
  let remainder = 0;
  for (const ch of withPlaceholder) {
    const code = ch.charCodeAt(0);
    const digits =
      code >= 48 && code <= 57 ? String(code - 48) : String(code - 55); // A=10…Z=35
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
  }
  const check = String(98 - remainder).padStart(2, "0");
  return `RF${check}${clean}`;
}

export function isValidCreditorReference(ref: string): boolean {
  const clean = ref.replace(/\s+/g, "").toUpperCase();
  if (!/^RF\d{2}[A-Z0-9]{1,21}$/.test(clean)) return false;
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    const digits =
      code >= 48 && code <= 57 ? String(code - 48) : String(code - 55);
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder === 1;
}

/** Validate a reference against the configured reference type (spec §11.5). */
export function validateReferenceCombination(input: {
  iban: string;
  referenceType: "NON" | "SCOR" | "QRR";
  reference: string | null;
}): { ok: true } | { ok: false; reason: string } {
  const iban = normalizeIban(input.iban);
  if (!isValidIban(iban)) return { ok: false, reason: "Invalid IBAN checksum" };
  const qrIban = isQrIban(iban);
  if (input.referenceType === "QRR") {
    if (!qrIban)
      return { ok: false, reason: "QRR reference requires a QR-IBAN" };
    if (!input.reference || !isValidQrReference(input.reference)) {
      return {
        ok: false,
        reason: "Invalid QR reference (27 digits, mod-10 check)",
      };
    }
  } else if (input.referenceType === "SCOR") {
    if (qrIban)
      return { ok: false, reason: "QR-IBAN requires a QRR reference" };
    if (!input.reference || !isValidCreditorReference(input.reference)) {
      return { ok: false, reason: "Invalid creditor reference (ISO 11649 RF)" };
    }
  } else {
    if (qrIban)
      return { ok: false, reason: "QR-IBAN requires a QRR reference" };
  }
  return { ok: true };
}

/** Group an IBAN or reference in blocks of 4/5 for human display. */
export function formatIban(iban: string): string {
  return normalizeIban(iban)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}
